import React, {
  ForwardedRef,
  MutableRefObject,
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from 'react';

import cn from 'classnames';
import useSWR from 'swr';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'next-i18next';

import { ButtonV3, InputV2 } from 'components/ComponentV2';
import Avatar from 'components/Avatar';
import ActionMenu from 'components/ActionMenu';
import Icon from 'components/Icon';
import { ActionMenuButton } from 'components/ActionMenuButton/ActionMenuButton.component';
import { TextArea } from 'components/form';
import Spinner from 'components/Spinner';
import { useResponsive } from 'hooks/useResponsive';
import {
  CoverLetterJobDataType,
  useAIGeneratedResponse,
} from 'pages/candidate/applications/useAIGeneratedResponse';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { apiInstance } from 'lib/utils/axios';
import { API_ROUTES } from 'lib/api-routes';
import {
  ManagerOpportunitiesResponse,
  OpportunityAttributes,
  OpportunityResponse,
} from 'lib/models/opportunity';
import { CompanyAttributes } from 'lib/models/company';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { useCandidateDashboard } from 'lib/providers/CandidateDashboardProvider';
import { useAuth } from 'lib/providers/AuthProvider';
import CoverLetterViewer from '../CoverLetterViewer/CoverLetterViewer.component';

import styles from './CoverLetterInfo.module.scss';

interface CoverLetterInfoProps {
  opportunityId?: string;
  goToEditProfile: () => void;
  setCoverLetterData: (data: string, companyTitle?: string) => void;
}

export type CoverLetterInfoRef = {
  builderStepsRef: MutableRefObject<HTMLDivElement | null>;
  coverLetterViewerRef: MutableRefObject<HTMLDivElement | null>;
};

const CoverLetterInfo = forwardRef<CoverLetterInfoRef, CoverLetterInfoProps>(
  (props: CoverLetterInfoProps, ref: ForwardedRef<CoverLetterInfoRef>) => {
    const { opportunityId, goToEditProfile, setCoverLetterData } = props;
    const [t] = useTranslation('candidate.cover-letter');

    const { builderStepsRef, coverLetterViewerRef } =
      ref as unknown as CoverLetterInfoRef;
    const screens = useResponsive();
    const { candidate } = useAuth();
    const { candidateDashboard } = useCandidateDashboard();
    const { profile_complete } = candidateDashboard || {};
    const {
      name = '',
      avatar_url,
      education_histories: educationResponse,
      huzzle_verified,
      show_cv_builder_popup,
    } = candidate || {};
    const { subject, subject_name, university, university_name } =
      parseArrayResponse(educationResponse)[0] || {};

    const [isCreateMode, setIsCreateMode] = useState<boolean>(true);
    const [generatedCoverLetter, setGeneratedCoverLetter] =
      useState<string>('');
    const [oldCoverLetter, setOldCoverLetter] = useState<string>('');
    const [selectedSavedJob, setSelectedSavedJob] = useState<
      OpportunityAttributes | undefined
    >(undefined);
    const [jobData, setJobData] = useState<CoverLetterJobDataType>(
      {} as CoverLetterJobDataType
    );

    const { isLoading, generateCoverLetter } = useAIGeneratedResponse();

    const { data: savedOpportunitiesResponse } = useSWR<
      AxiosResponse<ManagerOpportunitiesResponse>
    >(
      `${API_ROUTES.CAMPAIGNS}?action_type=saved&touchpointable_types[]=Job&touchpointable_types[]=Internship&with_details=true&page=1&per_page=1000`,
      apiInstance.get,
      {
        revalidateOnFocus: false,
      }
    );

    const { data: opportunityResponse, isLoading: fetchOpportunityLoading } =
      useSWR<AxiosResponse<OpportunityResponse>>(
        opportunityId ? `${API_ROUTES.CAMPAIGNS}/${opportunityId}` : null,
        apiInstance.get,
        {
          revalidateOnFocus: false,
        }
      );

    useEffect(() => {
      if (!fetchOpportunityLoading && opportunityResponse) {
        const data = parseResponse<OpportunityAttributes>(
          opportunityResponse.data
        );
        setSelectedSavedJob(data);
      }
    }, [fetchOpportunityLoading]);

    const savedOpportunities = useMemo(() => {
      if (!savedOpportunitiesResponse) return [];
      return parseArrayResponse<OpportunityAttributes>(
        savedOpportunitiesResponse.data
      );
    }, [savedOpportunitiesResponse]);

    const onSelectOpportunity = (opportunity?: OpportunityAttributes) => () => {
      setSelectedSavedJob(opportunity);
      setGeneratedCoverLetter('');
      setCoverLetterData('');
    };

    const onGenerateCoverLetter = async () => {
      const response = await generateCoverLetter(
        selectedSavedJob?.id || '',
        jobData,
        oldCoverLetter
      );
      if (response) {
        const companyName = selectedSavedJob
          ? selectedSavedJob.creatable_for.data.attributes.name
          : jobData.company_name;
        setGeneratedCoverLetter(response.suggestions);
        setCoverLetterData(response.suggestions, companyName);
      }
    };

    const disabledGenerateAction = useMemo(() => {
      if (!profile_complete) return true;
      if (selectedSavedJob) {
        return isCreateMode ? false : !oldCoverLetter;
      } else {
        const disable = isCreateMode ? false : !oldCoverLetter;
        return (
          disable ||
          !jobData.title ||
          !jobData.company_name ||
          !jobData.description
        );
      }
    }, [
      profile_complete,
      selectedSavedJob,
      isCreateMode,
      oldCoverLetter,
      jobData,
    ]);

    const manageProfileMenu = useMemo(
      () => [
        {
          id: '1',
          label: t('cover-letter-info.manager-profile'),
          onClick: goToEditProfile,
        },
      ],
      []
    );

    const builderModeOptions = useMemo(
      () => [
        {
          id: '1',
          label: t('cover-letter-info.create-new-cover-letter'),
          onClick: () => setIsCreateMode(true),
        },
        {
          id: '2',
          label: t('cover-letter-info.improve-existing-cover-letter'),
          onClick: () => setIsCreateMode(false),
        },
      ],
      [isCreateMode]
    );

    const savedJobsOptions = useMemo(() => {
      if (savedOpportunities.length) {
        return savedOpportunities.map((opportunity) => {
          return {
            id: opportunity.id,
            label: opportunity.title,
            onClick: onSelectOpportunity(opportunity),
          };
        });
      } else {
        return [
          {
            id: '1',
            label: (
              <p className={styles.emptyList}>
                {t('cover-letter-info.empty-list-text')}
              </p>
            ),
          },
        ];
      }
    }, [savedOpportunities]);

    const renderManageProfileStep = useMemo(
      () => (
        <div className={styles.stepContainer}>
          {show_cv_builder_popup && !screens.sm && (
            <div className={styles.stepCount}>
              {profile_complete ? (
                <Icon
                  iconName="icon_check"
                  size="small"
                  className={styles.checkIcon}
                />
              ) : (
                <p>1</p>
              )}
            </div>
          )}
          <div className={styles.profileStep}>
            <div className={styles.profileInfo}>
              <Avatar
                icon={avatar_url}
                name={name}
                altText={`Logo of ${name}`}
                size={'large'}
                isHuzzleVerified={huzzle_verified}
              />
              <div>
                <p className={styles.candidateName}>{name}</p>
                <p className={styles.candidateDetails}>
                  {subject?.data?.attributes?.name || subject_name}
                </p>
                <p className={styles.candidateDetails}>
                  {university?.data?.attributes?.name || university_name}
                </p>
              </div>
            </div>
            <ActionMenu
              options={manageProfileMenu}
              actionIcon="icon_arrow-down"
              triggerIconSize="xsmall"
              triggerIconClassName={styles.arrowIcon}
              listClassName={styles.manageProfileList}
            />
          </div>
        </div>
      ),
      [candidate, profile_complete, show_cv_builder_popup, screens]
    );

    const renderBuilderModeStep = useMemo(
      () => (
        <div className={cn(styles.stepContainer, styles.builderMode)}>
          {show_cv_builder_popup && !screens.sm && (
            <div className={styles.stepCount}>
              <p>2</p>
            </div>
          )}
          <ActionMenuButton
            options={builderModeOptions}
            buttonText={
              isCreateMode
                ? t('cover-letter-info.create-new-cover-letter')
                : t('cover-letter-info.improve-existing-cover-letter')
            }
            buttonEndIcon="icon_arrow-down"
            buttonColor="secondary"
            buttonSize="xlarge"
            buttonClassName={styles.builderModeButton}
            listClassName={styles.builderModeList}
            menuOpenClassName={styles.builderModeButtonActive}
          />
        </div>
      ),
      [isCreateMode, show_cv_builder_popup, screens]
    );

    const renderPasteCoverLetterStep = useMemo(
      () => (
        <div
          className={cn(styles.stepContainer, {
            [styles.disableStep]: !profile_complete,
          })}
        >
          {show_cv_builder_popup && !screens.sm && (
            <div className={styles.stepCount}>
              <p>3</p>
            </div>
          )}
          <TextArea
            placeholder={t('cover-letter-info.paste-cover-letter-to-improve')}
            textAreaClassName={styles.improveCoverLetter}
            isValid
            isDynamic={true}
            value={oldCoverLetter}
            onChange={(event) => setOldCoverLetter(event.target.value)}
            initialHeight="160px"
          />
        </div>
      ),
      [oldCoverLetter, show_cv_builder_popup, profile_complete, screens]
    );

    const renderJobCard = (opportunity: OpportunityAttributes) => {
      const { title, touchpointable_type, creatable_for } = opportunity;
      const { name, icon_url } = parseResponse<
        CompanyAttributes | StudentSocietyAttributes
      >(creatable_for);

      return (
        <>
          <div className={styles.jobDetailsCard}>
            <Avatar
              icon={icon_url}
              name={name}
              altText={`Logo of ${name}`}
              size="medium"
            />
            <div>
              <p className={styles.CardTitle}>{title}</p>
              <div className={styles.cardDetails}>
                <p>{touchpointable_type}</p>
                <Icon
                  iconName="icon_dot"
                  size="xxsmall"
                  className={styles.dotIcon}
                />
                <p>{name}</p>
              </div>
            </div>
          </div>
          <ButtonV3
            color="primary"
            variant="text"
            className={styles.manualEntry}
            onClick={onSelectOpportunity()}
          >
            {t('cover-letter-info.enter-manually')}
          </ButtonV3>
        </>
      );
    };

    const renderJobDetailsStep = useMemo(
      () => (
        <div
          className={cn(styles.stepContainer, {
            [styles.disableStep]: !profile_complete,
          })}
        >
          {show_cv_builder_popup && !screens.sm && (
            <div className={styles.stepCount}>
              <p>{isCreateMode ? 3 : 4}</p>
            </div>
          )}
          <div className={styles.jobDetails}>
            <div className={styles.jobDetailsSelection}>
              <p>{t('cover-letter-info.job-details')}</p>
              <ActionMenuButton
                options={savedJobsOptions}
                buttonText={t('cover-letter-info.select-from-saved-jobs')}
                buttonColor="secondary"
                buttonSize="small"
                buttonEndIcon="icon_arrow-down"
                listClassName={styles.jobDetailsList}
                disabled={!profile_complete}
              />
            </div>
            {!selectedSavedJob ? (
              <div className={styles.jobDetailsForm}>
                <InputV2
                  placeholder={t('cover-letter-info.job-title')}
                  inputClassName={styles.jobDetailsInput}
                  onChange={(event) =>
                    setJobData((data) => ({
                      ...data,
                      title: event.target.value,
                    }))
                  }
                />
                <InputV2
                  placeholder={t('cover-letter-info.company-name')}
                  inputClassName={styles.jobDetailsInput}
                  onChange={(event) =>
                    setJobData((data) => ({
                      ...data,
                      company_name: event.target.value,
                    }))
                  }
                />
                <TextArea
                  placeholder={t('cover-letter-info.job-description')}
                  textAreaClassName={cn(
                    styles.improveCoverLetter,
                    styles.jobDetailsText
                  )}
                  onChange={(event) =>
                    setJobData((data) => ({
                      ...data,
                      description: event.target.value,
                    }))
                  }
                />
              </div>
            ) : (
              renderJobCard(selectedSavedJob)
            )}
          </div>
        </div>
      ),
      [
        isCreateMode,
        selectedSavedJob,
        savedJobsOptions,
        show_cv_builder_popup,
        profile_complete,
        screens,
      ]
    );

    const renderAction = useMemo(
      () => (
        <div
          className={cn(styles.builderStepsAction, {
            [styles.builderStepsMobileAction]: screens.sm,
          })}
        >
          {show_cv_builder_popup && !screens.sm && (
            <div className={styles.stepCount}>
              <p>{isCreateMode ? 4 : 5}</p>
            </div>
          )}
          <ButtonV3
            size="large"
            startIcon="icon_magic-wand"
            isFullWidth
            color={generatedCoverLetter ? 'secondary' : 'primary'}
            disabled={disabledGenerateAction}
            onClick={onGenerateCoverLetter}
          >
            {generatedCoverLetter
              ? t('cover-letter-info.regenerate')
              : t('cover-letter-info.generate')}
          </ButtonV3>
        </div>
      ),
      [
        show_cv_builder_popup,
        isCreateMode,
        generatedCoverLetter,
        profile_complete,
        disabledGenerateAction,
        screens,
      ]
    );

    return (
      <>
        {(isLoading || fetchOpportunityLoading) && (
          <Spinner
            className={styles.spinner}
            wrapper
            showBackDrop
            size="small"
          />
        )}
        <div
          className={cn(styles.CoverLetterInfo, {
            [styles.topPadding]: profile_complete,
          })}
        >
          <div
            className={cn(styles.builderStepsWrapper, {
              [styles.topClearance]: show_cv_builder_popup && !screens.sm,
            })}
            ref={builderStepsRef}
          >
            <div className={styles.builderSteps}>
              {renderManageProfileStep}
              {renderBuilderModeStep}
              {!isCreateMode && renderPasteCoverLetterStep}
              {renderJobDetailsStep}
              {!screens.sm && renderAction}
            </div>
          </div>
          <div
            ref={coverLetterViewerRef}
            className={styles.coverLetterViewerWrapper}
          >
            <CoverLetterViewer
              isProfileComplete={!!profile_complete}
              coverLetterData={generatedCoverLetter}
              companyName={
                selectedSavedJob
                  ? selectedSavedJob.creatable_for.data.attributes.name
                  : jobData.company_name
              }
            />
          </div>
          {screens.sm && renderAction}
        </div>
      </>
    );
  }
);

export default CoverLetterInfo;
